import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import {
  X, Heart, UtensilsCrossed, Sparkles, Refrigerator, Loader2, ChevronRight,
  CookingPot, Salad, Sandwich
} from "lucide-react";
import CookTimeSlider, { COOK_TIME_OPTIONS } from "@/components/CookTimeSlider";
import { isLocked } from "@/utils/subscription";
import { CrownIcon } from "@/components/LockBadge";

const DISH_SUBTYPES = ["main_course", "side_dish", "salad", "appetizer"];

export default function AddDishModal({ open, mealType, planId, date, onClose, onAdded }) {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const locked = isLocked(user);

  const [addDishStep, setAddDishStep] = useState(1); // 1=source, 2=ai subtype, 3=custom
  const [addDishSubtype, setAddDishSubtype] = useState(null);
  const [addDishLoading, setAddDishLoading] = useState(false);
  const [cookTimeIdx, setCookTimeIdx] = useState(4); // cook-time slider; 4 = no limit
  const maxCookTime = COOK_TIME_OPTIONS[cookTimeIdx]; // minutes or null
  const [customDishTitle, setCustomDishTitle] = useState("");
  const [customDishDesc, setCustomDishDesc] = useState("");
  const [genError, setGenError] = useState("");

  const subtypeIcons = {
    main_course: UtensilsCrossed,
    side_dish: CookingPot,
    salad: Salad,
    appetizer: Sandwich,
  };

  const closeAddDish = () => {
    setAddDishStep(1);
    setAddDishSubtype(null);
    setAddDishLoading(false);
    setCookTimeIdx(4);
    setCustomDishTitle("");
    setCustomDishDesc("");
    setGenError("");
    onClose();
  };

  const handleAddDishFromFridge = async () => {
    if (!planId || !mealType) return;
    setAddDishSubtype("__fridge__");
    setAddDishLoading(true);
    setGenError("");
    try {
      await api().post("/meal-plans/add-dish", {
        plan_id: planId, date: date,
        meal_type: mealType, dish_subtype: "main_course",
        mode: "generate", fridge_only: true, max_cook_time: maxCookTime,
      }, { redirectOnPaywall: true });
      await onAdded();
      closeAddDish();
    } catch (err) {
      console.error(err);
      await onAdded();
      setGenError(t("genFailed"));
    }
    setAddDishLoading(false);
  };

  const handleAddDishGenerate = async (subtype) => {
    if (!planId || !mealType) return;
    setAddDishSubtype(subtype);
    setAddDishLoading(true);
    setGenError("");
    try {
      await api().post("/meal-plans/add-dish", {
        plan_id: planId, date: date,
        meal_type: mealType, dish_subtype: subtype,
        mode: "generate", max_cook_time: maxCookTime,
      }, { redirectOnPaywall: true });
      await onAdded();
      localStorage.setItem("mealpill_grocery_stale", "true");
      closeAddDish();
    } catch (err) {
      console.error(err);
      await onAdded();
      setGenError(t("genFailed"));
    }
    setAddDishLoading(false);
  };

  const handleAddDishCustom = async () => {
    if (!planId || !mealType || !customDishTitle.trim()) return;
    setAddDishLoading(true);
    try {
      await api().post("/meal-plans/add-dish", {
        plan_id: planId, date: date,
        meal_type: mealType, dish_subtype: addDishSubtype,
        mode: "custom", custom_title: customDishTitle.trim(),
        custom_description: customDishDesc.trim() || null,
      });
      await onAdded();
      closeAddDish();
    } catch (err) {
      console.error(err);
      await onAdded();
    }
    setAddDishLoading(false);
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end" data-testid="add-dish-modal">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl max-h-[92vh] overflow-y-auto p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl text-[#3D4035]">
                {addDishStep === 1 && t("addDish")}
                {addDishStep === 2 && t("addDishSelectType")}
                {addDishStep === 3 && t("addDishCustom")}
              </h3>
              <button onClick={closeAddDish} className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center">
                <X className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
              </button>
            </div>

            {genError && (
              <div data-testid="gen-error" className="text-[#BC4749] bg-[#BC4749]/10 rounded-xl px-4 py-3 mb-4 text-sm">
                {genError}
              </div>
            )}

            {/* Step 1: Choose source - Favorites, My Recipes, AI Generate */}
            {addDishStep === 1 && (
              <div className="space-y-3">
                <button data-testid="add-from-favorites"
                  onClick={() => navigate(`/dishes?tab=favorites&addTo=${mealType}&planId=${planId || ""}&date=${date}`)}
                  className="w-full bg-white text-[#3D4035] rounded-2xl p-5 font-body font-bold text-left border border-[#E8E5E0] hover:border-[#E9C46A] hover:bg-[#E9C46A]/5 transition-all flex items-center gap-3">
                  <Heart className="w-6 h-6 text-[#E9C46A] shrink-0" strokeWidth={1.5} fill="#E9C46A" />
                  <div>
                    <p className="font-semibold">{t("favorites")}</p>
                    <p className="text-xs text-[#A5A58D] font-normal mt-0.5">{t("addFromFavoritesDesc")}</p>
                  </div>
                </button>
                <button data-testid="add-from-my-recipes"
                  onClick={() => navigate(`/dishes?tab=myRecipes&addTo=${mealType}&planId=${planId || ""}&date=${date}`)}
                  className="w-full bg-white text-[#3D4035] rounded-2xl p-5 font-body font-bold text-left border border-[#E8E5E0] hover:border-[#7B9E7F] hover:bg-[#7B9E7F]/5 transition-all flex items-center gap-3">
                  <UtensilsCrossed className="w-6 h-6 text-[#7B9E7F] shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-semibold">{t("myRecipes")}</p>
                    <p className="text-xs text-[#A5A58D] font-normal mt-0.5">{t("addFromMyRecipesDesc")}</p>
                  </div>
                </button>
                <div className="flex gap-2">
                  <button data-testid="add-dish-ai-generate"
                    onClick={locked ? () => { navigate("/subscription"); onClose?.(); } : () => setAddDishStep(2)}
                    className="relative flex-[65] bg-[#7B9E7F] text-white rounded-2xl p-5 font-body font-bold text-left hover:bg-[#6B8E6F] transition-all flex items-center gap-3">
                    {locked && <CrownIcon className="absolute top-2 right-2" />}
                    <Sparkles className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                    <div>
                      <p className="font-semibold text-sm">{t("aiGeneration")}</p>
                      <p className="text-xs opacity-80 font-normal mt-0.5">{t("aiGenerationDesc")}</p>
                    </div>
                  </button>
                  <button data-testid="add-dish-from-fridge"
                    onClick={locked ? () => { navigate("/subscription"); onClose?.(); } : () => handleAddDishFromFridge()}
                    disabled={addDishLoading}
                    className="relative flex-[35] bg-[#EBE9E4] text-[#4A6B4E] rounded-2xl p-4 font-body font-bold text-center hover:bg-[#E0DDD7] transition-all flex flex-col items-center justify-center gap-1.5 disabled:opacity-50">
                    {locked && <CrownIcon className="absolute top-2 right-2" />}
                    {addDishLoading && addDishSubtype === "__fridge__" ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Refrigerator className="w-5 h-5" strokeWidth={1.5} />
                    )}
                    <span className="text-xs font-semibold leading-tight">{t("fromFridge") || "З холодильника"}</span>
                  </button>
                </div>
                <CookTimeSlider valueIdx={cookTimeIdx} onChange={setCookTimeIdx} disabled={addDishLoading} className="pt-2" />
              </div>
            )}

            {/* Step 2: Select dish subtype for AI generation */}
            {addDishStep === 2 && (
              <div className="space-y-4">
                <button onClick={() => setAddDishStep(1)} className="flex items-center gap-1.5 text-[#6B705C] font-body text-sm mb-2">
                  <ChevronRight className="w-4 h-4 rotate-180" strokeWidth={1.5} />
                  {t("back")}
                </button>
                <div className="grid grid-cols-2 gap-3">
                  {DISH_SUBTYPES.map((subtype) => {
                    const Icon = subtypeIcons[subtype] || UtensilsCrossed;
                    return (
                      <button key={subtype} data-testid={`dish-subtype-${subtype}`}
                        onClick={() => { setAddDishSubtype(subtype); handleAddDishGenerate(subtype); }}
                        disabled={addDishLoading}
                        className="bg-white rounded-2xl p-5 text-center border border-[#E8E5E0] hover:border-[#7B9E7F] hover:shadow-md transition-all group disabled:opacity-50">
                        {addDishLoading && addDishSubtype === subtype ? (
                          <Loader2 className="w-7 h-7 text-[#7B9E7F] mx-auto mb-2 animate-spin" />
                        ) : (
                          <Icon className="w-7 h-7 text-[#7B9E7F] mx-auto mb-2 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                        )}
                        <span className="font-body font-semibold text-[#3D4035] text-sm">{t(subtype)}</span>
                      </button>
                    );
                  })}
                </div>
                <CookTimeSlider valueIdx={cookTimeIdx} onChange={setCookTimeIdx} disabled={addDishLoading} className="pt-1" />
              </div>
            )}

            {addDishStep === 3 && (
              <div className="space-y-3">
                <input data-testid="custom-dish-title" type="text" value={customDishTitle}
                  onChange={(e) => setCustomDishTitle(e.target.value)}
                  placeholder={t("dishTitlePlaceholder")}
                  className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none" autoFocus />
                <input data-testid="custom-dish-desc" type="text" value={customDishDesc}
                  onChange={(e) => setCustomDishDesc(e.target.value)}
                  placeholder={t("dishDescPlaceholder")}
                  className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none" />
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setAddDishStep(2)} className="flex-1 bg-[#EBE9E4] text-[#6B705C] rounded-full h-12 font-body font-bold">{t("back")}</button>
                  <button data-testid="custom-dish-save" onClick={handleAddDishCustom} disabled={!customDishTitle.trim() || addDishLoading}
                    className="flex-1 bg-[#7B9E7F] text-white rounded-full h-12 font-body font-bold hover:bg-[#6B8E6F] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    {addDishLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t("save")}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </>
  );
}
